import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useProducts } from '@/contexts/productContext.tsx'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useDrives } from '@/stores/useDrives.ts'
import { FilesProvider, useFilesContext } from '@/contexts/filesContext.tsx'
import { Album } from '@/models/album.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import UnifiedImageSelectionView from '@/components/UnifiedImageSelectionView.tsx'
import { bookFromAlbumFilesSmart, TooManyImagesError } from '@/pages/BookDesign.page.tsx'
import { modParamsFromURL, modParamsToAPI, useBasket } from '@/contexts/basketContext.tsx'

export default function ProductSelectPhotoPage() {
  // path will be like /:product_sku/:sku_suffix
  const { product_sku, sku_suffix, driveId } = useParams<{ product_sku: string; sku_suffix: string; driveId: string }>()
  const { products, isLoading } = useProducts()
  const navigate = useNavigate()
  const location = useLocation()
  const { addItem } = useBasket()

  // Get mod parameters from URL
  const modParams = new URLSearchParams(location.search)

  // Storage key for persisting selection
  const storageKey = `product-selection-${product_sku}-${sku_suffix}`

  // Get data from navigation state (when returning from crop page)
  const locationState = location.state as { selectedImages?: AlbumFile[], cropData?: any }
  const navigationSelectedImages = locationState?.selectedImages || []

  // Try to get from sessionStorage if no navigation state
  const getStoredSelection = (): AlbumFile[] => {
    try {
      const stored = sessionStorage.getItem(storageKey)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  const initialSelectedImages = navigationSelectedImages.length > 0
    ? navigationSelectedImages
    : getStoredSelection()

  // Find product by SKU
  const product = products.find(p => p.sku === product_sku)

  // Find the specific variation by sku_suffix
  const variation = product?.variations.find(v => v.sku_suffix === sku_suffix)

  async function handleAddToBasket(selectedImages: AlbumFile[]) {
    if (!product || !variation || selectedImages.length === 0) return

    // Store selection in sessionStorage before navigating
    sessionStorage.setItem(storageKey, JSON.stringify(selectedImages))

    // Navigate to crop page with selected images and preserve mod parameters
    const modParamsString = modParams.toString()
    if (product.sku === 'photobook') {
      const defaultWidth = variation!.property_value_links.find(prop => prop.property_value.width_mm != null)!.property_value.width_mm!
      const defaultHeight = variation!.property_value_links.find(prop => prop.property_value.height_mm != null)!.property_value.height_mm!

      let bookData
      try {
        bookData = bookFromAlbumFilesSmart(selectedImages, defaultWidth*12, defaultHeight*12)
      } catch (error) {
        if (error instanceof TooManyImagesError) {
          alert(`Du hast zu viele Bilder ausgewählt (${error.requestedImages}). Maximal ${error.maxImages} Bilder sind möglich.`)
          return
        }
        throw error
      }

      const modParams = modParamsFromURL()
      const modsForAPI = modParamsToAPI(modParams, product)

      const crop_data: Record<string, any> = {}
      bookData.images.forEach((item) => crop_data[item.img!.id] = item)

      const basketItem: any = await addItem({
        product: product.id,
        variation: variation.id,
        quantity: 1,
        files: bookData.images.map(img => img.img!.id),
        crop_data,
        mods: modsForAPI,
        pages: bookData.pages,
        page_width: bookData.book.page_width,
        page_height: bookData.book.page_height,
        drive_id: driveId!,
      })

      navigate(`/d/${driveId}/shop/basket/edit/${basketItem.data.id}`)
      return
    }

    navigate(`crop-photos${modParamsString ? `?${modParamsString}` : ''}`, {
      state: {
        selectedImages
      }
    })
  }

  // Clean up sessionStorage when component unmounts or when going to basket
  useEffect(() => {
    return () => {
      // Only clean up if we're navigating away from the product flow
      const currentPath = window.location.pathname
      if (!currentPath.includes(product_sku!) || !currentPath.includes(sku_suffix!)) {
        sessionStorage.removeItem(storageKey)
      }
    }
  }, [storageKey, product_sku, sku_suffix])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (!product || !variation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">Produkt oder Variante nicht gefunden</h1>
          <p className="text-center opacity-75">Das angeforderte Produkt oder die Variante konnte nicht gefunden werden.</p>
          <Button color="primary" variant="outline" onClick={() => navigate(`/d/${driveId}/shop`)}>
            <IconArrowBack size={20} />
            Zurück zum Shop
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Product and variation info */}
      <div className="bg-base-200 p-6 rounded-lg mb-6">
        <h1 className="text-2xl font-bold mb-2">{product.title}</h1>
        <h2 className="text-lg font-semibold text-primary mb-2">{variation.full_title}</h2>
        {variation.property_value_display && (
          <p className="text-sm mt-1">{variation.property_value_display}</p>
        )}
      </div>

      {/* Photo selection component */}
      <ProductPhotoSelectComponent
        minImageCount={product.min_image_count}
        maxImageCount={product.max_image_count}
        onContinue={handleAddToBasket}
        initialSelectedImages={initialSelectedImages}
        onBack={() => {
          const modParamsString = modParams.toString()
          navigate(`/d/${driveId}/shop/product/${product_sku}${modParamsString ? `?${modParamsString}` : ''}`)
        }}
      />
    </div>
  )
}

function ProductPhotoSelectComponent({
  minImageCount,
  maxImageCount,
  onContinue,
  initialSelectedImages = [],
  onBack
}: {
  minImageCount: number | null,
  maxImageCount: number | null,
  onContinue: (selectedImages: AlbumFile[]) => void,
  initialSelectedImages?: AlbumFile[],
  onBack: () => void
}) {
  const { currentDrive } = useDrives()
  const { currentAlbum: currentOuterAlbum } = useFilesContext()!
  const [currentAlbum, setCurrentAlbum] = useState(currentOuterAlbum!)
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>(initialSelectedImages)
  const { product_sku, sku_suffix } = useParams<{ product_sku: string; sku_suffix: string }>()

  // Storage key for persisting selection
  const storageKey = `product-selection-${product_sku}-${sku_suffix}`

  // Save to sessionStorage whenever selectedImages changes
  useEffect(() => {
    if (selectedImages.length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(selectedImages))
    } else {
      sessionStorage.removeItem(storageKey)
    }
  }, [selectedImages, storageKey])

  return (
    <div className="flex flex-col gap-6">
      {/* Combined album selection and image selection */}
      <div className="bg-base-200 p-4 rounded-lg">
        <div className="mb-4">
          <AlbumSelection
            drive={currentDrive!}
            selectedAlbum={currentAlbum!}
            setSelectedAlbum={(album: Album) => setCurrentAlbum(album)}
          />
        </div>

        <FilesProvider albumId={currentAlbum.id} filesFilter={{media: AlbumFileType.IMAGE}}>
          <UnifiedImageSelectionView
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            minImageCount={minImageCount}
            maxImageCount={maxImageCount}
            initialSelectedImages={initialSelectedImages}
            onContinue={() => onContinue(selectedImages)}
            onBack={onBack}
          />
        </FilesProvider>
      </div>
    </div>
  )
}

